import React, { useEffect, useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { API_BASE } from "@/App";
import { ChevronDown, ChevronUp } from "lucide-react";

const CurrentAffairsPage = () => {
  const { user } = useAuth();
  const [view, setView] = useState("monthly"); // monthly or topics
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [examFocus, setExamFocus] = useState("UPSC");
  const [expandedItems, setExpandedItems] = useState(new Set()); // Track expanded items
  
  // Monthly view
  const [selectedMonth, setSelectedMonth] = useState(10); // October by default
  const [selectedYear, setSelectedYear] = useState(2025);
  
  // Topic view
  const [selectedTheme, setSelectedTheme] = useState("Polity");
  
  // Toggle item expansion
  const toggleExpand = (idx) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedItems(newExpanded);
  };

  const isPro = user?.role === "PRO";

  // Remove auto-load on mount - user must click "Load CA" button manually

  const loadMonthlyCA = async () => {
    if (!isPro) {
      alert("Monthly archives are PRO only. Upgrade to unlock!");
      navigate("/upgrade");
      return;
    }
    try {
      setLoading(true);
      const { data } = await managedAxios.get(
        `${API_BASE}/ca/monthly?month=${selectedMonth}&year=${selectedYear}&exam_focus=${examFocus}`
      );
      setItems(data.items || []);
    } catch (e) {
      console.error("Error loading monthly CA:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTopicWiseCA = async () => {
    if (!isPro) {
      alert("Topic-wise CA is PRO only. Upgrade to unlock!");
      navigate("/upgrade");
      return;
    }
    try {
      setLoading(true);
      const { data } = await managedAxios.get(`${API_BASE}/ca/topics?theme=${selectedTheme}&exam_focus=${examFocus}`);
      setItems(data.items || []);
    } catch (e) {
      console.error("Error loading topic CA:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const themes = [
    "Polity", 
    "Economy", 
    "Environment", 
    "International Relations", 
    "Science & Technology", 
    "Social Issues", 
    "Geography", 
    "History"
  ];
  
  const months = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November"
  ];

  return (
    <div className="min-h-screen bg-slate-50/30">
      {/* Premium Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-purple-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-violet-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-10 space-y-8">
      {/* Header - Premium Glassmorphism */}
      <div className="glass-card rounded-2xl p-6 md:p-8 animate-fade-up">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-1">
              <Badge className={`px-3 py-1 ${isPro 
                ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white border-0' 
                : 'bg-slate-100 text-slate-600 border-0'}`}>
                {isPro ? 'PRO Access' : 'FREE Access'}
              </Badge>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Current Affairs OS</h1>
            <p className="text-slate-600">
              RAG-powered CA from Jan-Nov 2025
            </p>
          </div>
          
          {/* Exam Focus Selector */}
          <div className="flex gap-2">
            {["UPSC", "APPSC", "TGPSC"].map((exam) => (
              <Button
                key={exam}
                size="sm"
                variant={examFocus === exam ? "default" : "outline"}
                onClick={() => setExamFocus(exam)}
                className={examFocus === exam 
                  ? "bg-gradient-to-r from-purple-500 to-violet-500 text-white border-0 hover:opacity-90" 
                  : "hover:bg-purple-50 hover:border-purple-300 hover:text-purple-700"}
              >
                {exam}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* View Tabs - Premium Style */}
      <div className="flex gap-2 animate-fade-up" style={{animationDelay: '100ms'}}>
        <Button
          size="sm"
          variant={view === "monthly" ? "default" : "outline"}
          onClick={() => setView("monthly")}
          className={view === "monthly" 
            ? "bg-gradient-to-r from-purple-500 to-violet-500 text-white border-0" 
            : "hover:bg-purple-50 hover:border-purple-300"}
        >
          📆 Monthly Archives {!isPro && "🔒"}
        </Button>
        <Button
          size="sm"
          variant={view === "topics" ? "default" : "outline"}
          onClick={() => setView("topics")}
          className={view === "topics" 
            ? "bg-gradient-to-r from-purple-500 to-violet-500 text-white border-0" 
            : "hover:bg-purple-50 hover:border-purple-300"}
        >
          📚 Topics {!isPro && "🔒"}
        </Button>
      </div>

      {/* Monthly View */}
      {view === "monthly" && (
        <div className="space-y-4">
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-slate-900">Select Month for Current Affairs</h2>
                  <p className="text-xs text-slate-600 mt-1">
                    Get comprehensive current affairs from any month (Jan-Nov 2025)
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="border border-slate-300 rounded px-4 py-2 text-sm font-medium"
                  >
                    {months.map((month, idx) => (
                      <option key={idx} value={idx + 1}>
                        {month} 2025
                      </option>
                    ))}
                  </select>
                  <Button 
                    onClick={loadMonthlyCA} 
                    disabled={loading || !isPro}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? "Loading..." : "Load CA"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {!isPro && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-sm text-amber-800">
                  🔒 <strong>PRO Feature:</strong> Upgrade to access month-wise comprehensive current affairs archives from Jan-Nov 2025!
                </p>
              </CardContent>
            </Card>
          )}

          {isPro && items.length > 0 && (
            <div className="flex items-center justify-between bg-white p-3 rounded border">
              <div>
                <p className="font-semibold text-slate-900">
                  {months[selectedMonth - 1]} 2025 Current Affairs
                </p>
                <p className="text-xs text-slate-600">
                  {items.length} comprehensive CA items
                </p>
              </div>
              <Badge variant="outline" className="text-sm">
                {examFocus}
              </Badge>
            </div>
          )}

          <div className="grid gap-3">
            {items.map((item, idx) => {
              const isExpanded = expandedItems.has(idx);
              return (
              <Card key={idx} className="border-purple-200 hover:shadow-lg transition-all">
                <CardContent className="p-4 space-y-3">
                  <div 
                    className="flex items-start justify-between gap-3 cursor-pointer"
                    onClick={() => toggleExpand(idx)}
                  >
                    <h3 className="font-semibold text-slate-900 flex-1 hover:text-purple-600 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {item.theme}
                      </Badge>
                      {item.gs_paper && (
                        <Badge className="text-xs bg-blue-600">
                          {item.gs_paper}
                        </Badge>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-purple-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Description - truncated when collapsed */}
                  {item.description && (
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {isExpanded 
                        ? item.description 
                        : `${item.description.substring(0, 120)}${item.description.length > 120 ? '...' : ''}`
                      }
                    </p>
                  )}
                  
                  {/* Show full details only when expanded */}
                  {isExpanded && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {item.importance && (
                          <Badge variant={item.importance === "High" ? "default" : "secondary"} className="text-xs bg-purple-600">
                            {item.importance} Priority
                          </Badge>
                        )}
                      </div>
                      
                      {/* Mains Angle */}
                      {item.mains_angle && (
                        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                          <p className="text-sm font-semibold text-green-900 mb-2">📖 Mains Question Angle</p>
                          <p className="text-sm text-green-800">{item.mains_angle}</p>
                        </div>
                      )}
                      
                      {/* Keywords */}
                      {item.keywords && item.keywords.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-700 mb-2">🏷️ Keywords</p>
                          <div className="flex flex-wrap gap-2">
                            {item.keywords.map((kw, i) => (
                              <span key={i} className="text-xs bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full font-medium">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
              );
            })}
            
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <p className="text-slate-500 mt-3">Loading current affairs...</p>
              </div>
            )}
            
            {!loading && isPro && items.length === 0 && (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <p className="text-slate-500">Select a month and click &quot;Load CA&quot; to view current affairs</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Topics View */}
      {view === "topics" && (
        <div className="space-y-4">
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-slate-900">Topic-Wise Current Affairs</h2>
                  <p className="text-xs text-slate-600 mt-1">
                    Browse current affairs by specific themes and topics
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedTheme}
                    onChange={(e) => setSelectedTheme(e.target.value)}
                    className="border border-slate-300 rounded px-4 py-2 text-sm font-medium"
                  >
                    {themes.map((theme) => (
                      <option key={theme} value={theme}>{theme}</option>
                    ))}
                  </select>
                  <Button 
                    onClick={loadTopicWiseCA} 
                    disabled={loading || !isPro}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? "Loading..." : "Load Topic"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {!isPro && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-sm text-amber-800">
                  🔒 <strong>PRO Feature:</strong> Upgrade to access topic-wise organized current affairs with PYQ linkages!
                </p>
              </CardContent>
            </Card>
          )}

          {isPro && items.length > 0 && (
            <div className="flex items-center justify-between bg-white p-3 rounded border">
              <div>
                <p className="font-semibold text-slate-900">
                  {selectedTheme} - Current Affairs
                </p>
                <p className="text-xs text-slate-600">
                  {items.length} CA items found
                </p>
              </div>
              <Badge variant="outline" className="text-sm">
                {examFocus}
              </Badge>
            </div>
          )}

          <div className="grid gap-3">
            {items.map((item, idx) => {
              const isExpanded = expandedItems.has(idx);
              return (
              <Card key={idx} className="border-purple-200 hover:shadow-lg transition-all">
                <CardContent className="p-4 space-y-3">
                  <div 
                    className="flex items-start justify-between gap-3 cursor-pointer"
                    onClick={() => toggleExpand(idx)}
                  >
                    <h3 className="font-semibold text-slate-900 flex-1 hover:text-purple-600 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="outline" className="text-xs">
                        {item.theme}
                      </Badge>
                      {item.gs_paper && (
                        <Badge className="text-xs bg-blue-600">
                          {item.gs_paper}
                        </Badge>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-purple-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Description - truncated when collapsed */}
                  {item.description && (
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {isExpanded 
                        ? item.description 
                        : `${item.description.substring(0, 120)}${item.description.length > 120 ? '...' : ''}`
                      }
                    </p>
                  )}
                  
                  {/* Show full details only when expanded */}
                  {isExpanded && (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {item.importance && (
                          <Badge variant={item.importance === "High" ? "default" : "secondary"} className="text-xs bg-purple-600">
                            {item.importance} Priority
                          </Badge>
                        )}
                      </div>
                      
                      {/* Mains Angle */}
                      {item.mains_angle && (
                        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                          <p className="text-sm font-semibold text-green-900 mb-2">📖 Mains Question Angle</p>
                          <p className="text-sm text-green-800">{item.mains_angle}</p>
                        </div>
                      )}
                      
                      {/* Keywords */}
                      {item.keywords && item.keywords.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-slate-700 mb-2">🏷️ Keywords</p>
                          <div className="flex flex-wrap gap-2">
                            {item.keywords.map((kw, i) => (
                              <span key={i} className="text-xs bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full font-medium">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
              );
            })}
            
            {loading && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                <p className="text-slate-500 mt-3">Loading topic-wise CA...</p>
              </div>
            )}
            
            {!loading && isPro && items.length === 0 && (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <p className="text-slate-500">Select a topic and click &quot;Load Topic&quot; to view related current affairs</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CurrentAffairsPage;
